import { Flex, Text } from '@chakra-ui/react';
import { Checkbox } from '@morressier/alembic';
import {
  FormFieldCheckbox,
  FormFieldDependentSelect,
  FormFieldPublishingModel,
  FormFieldRadio
} from '@publishing-engine/domain/types/article';
import { ISubmissionFieldResponseObject } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import { GenericFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/generic-form-fields/generic-form-fields';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { parseStringWithLinks } from 'src/utils/parseStringWithLinks';

export const ReadonlyCopyrightForm: React.FC<{}> = () => {
  const {
    copyrightForm,
    workflowResponse: {
      forms: { authorCopyrightSubmission }
    },
    fieldsResponse
  } = useSubmissionData();

  const publishingModelFFD = fieldsResponse.fields[
    'PublishingModel'
  ] as ISubmissionFieldResponseObject<FormFieldPublishingModel>;
  const currentPublishingModel = publishingModelFFD?.value;

  const copyrightFormFields = new GenericFormFields(authorCopyrightSubmission!);

  const config = {
    CopyrightFormOwner: copyrightFormFields.getFieldConfig(
      'CopyrightFormOwner'
    ) as FormFieldDependentSelect,
    CopyrightFormWaiver: copyrightFormFields.getFieldConfig(
      'CopyrightFormWaiver'
    ) as FormFieldCheckbox,
    CopyrightFormType: copyrightFormFields.getFieldConfig(
      'CopyrightFormType'
    ) as FormFieldRadio,
    CopyrightFormProcessingCharge: copyrightFormFields.getFieldConfig(
      'CopyrightFormProcessingCharge'
    ) as FormFieldCheckbox,
    CopyrightFormTypeReason: copyrightFormFields.getFieldConfig(
      'CopyrightFormTypeReason'
    ),
    CopyrightFormSubscriptionAgreement: copyrightFormFields.getFieldConfig(
      'CopyrightFormSubscriptionAgreement'
    ) as FormFieldCheckbox,
    CopyrightFormOAAgreement: copyrightFormFields.getFieldConfig(
      'CopyrightFormOAAgreement'
    ) as FormFieldCheckbox,
    CopyrightFormAuthorsFullNames: copyrightFormFields.getFieldConfig(
      'CopyrightFormAuthorsFullNames'
    ),
    CopyrightFormAuthorSignature: copyrightFormFields.getFieldConfig(
      'CopyrightFormAuthorSignature'
    )
  };

  return (
    <>
      {copyrightForm && (
        <Flex flexDirection="column" gap={8}>
          {copyrightForm.ownership && config.CopyrightFormOwner && (
            <Flex
              flexDirection="column"
              padding={6}
              gap={4}
              borderRadius="base"
              background="neutralPrimaryFill"
            >
              <Flex flexDirection="column">
                <Text textStyle="headingItem">
                  {config.CopyrightFormOwner.params.labels.main}
                </Text>
                <Text textStyle="textBody">
                  {
                    config.CopyrightFormOwner.params.labels.options[
                      copyrightForm.ownership.exists.toString()
                    ]?.main
                  }
                </Text>
              </Flex>
              {copyrightForm.ownership.exists && (
                <Flex flexDirection="column">
                  <Text textStyle="headingItem">
                    {config.CopyrightFormOwner.params.labels.section.title}
                  </Text>
                  <Text textStyle="textBody">
                    {copyrightForm.ownership.value}
                  </Text>
                </Flex>
              )}
            </Flex>
          )}
          {(copyrightForm.waiver ||
            copyrightForm.articleProcessingCharge ||
            copyrightForm.type) && (
            <Flex
              flexDirection="column"
              padding={6}
              gap={4}
              borderRadius="base"
              background="neutralPrimaryFill"
            >
              <Flex flexDirection="column">
                <Text textStyle="headingItem">Publishing Model</Text>
                <Text textStyle="textBody">
                  The selected publishing model for this Submission is{' '}
                  {
                    publishingModelFFD?.formFieldDefinition.params.labels
                      .options[currentPublishingModel?.model!]?.main!
                  }
                  .
                </Text>
              </Flex>
              {copyrightForm.waiver && config.CopyrightFormWaiver && (
                <Flex flexDirection="column" gap={4}>
                  <Text textStyle="headingItem">
                    {config.CopyrightFormWaiver.params.labels.main}
                  </Text>
                  <Checkbox
                    ariaLabel={config.CopyrightFormWaiver.params.labels.main}
                    id="waivers"
                    name="waivers"
                    isChecked
                    isDisabled
                  >
                    {config.CopyrightFormWaiver.params.labels.content
                      ? parseStringWithLinks(
                          config.CopyrightFormWaiver.params.labels.content
                        )
                      : parseStringWithLinks(
                          config.CopyrightFormWaiver.params.labels.subtitle
                        )}
                  </Checkbox>
                </Flex>
              )}
              {copyrightForm.articleProcessingCharge &&
                config.CopyrightFormProcessingCharge && (
                  <Flex flexDirection="column" gap={4}>
                    <Text textStyle="headingItem">
                      {config.CopyrightFormProcessingCharge.params.labels.main}
                    </Text>
                    <Checkbox
                      ariaLabel={
                        config.CopyrightFormProcessingCharge.params.labels.main
                      }
                      id="waivers"
                      name="waivers"
                      isChecked
                      isDisabled
                    >
                      {config.CopyrightFormProcessingCharge.params.labels
                        .content
                        ? parseStringWithLinks(
                            config.CopyrightFormProcessingCharge.params.labels
                              .content
                          )
                        : parseStringWithLinks(
                            config.CopyrightFormProcessingCharge.params.labels
                              .subtitle
                          )}
                    </Checkbox>
                  </Flex>
                )}
              {copyrightForm.type && config.CopyrightFormType && (
                <Flex flexDirection="column">
                  <Text textStyle="headingItem">
                    {config.CopyrightFormType.params.labels.main}
                  </Text>
                  <Text textStyle="textBody">
                    {
                      config.CopyrightFormType.params.labels.options[
                        copyrightForm.type
                      ]?.main
                    }
                  </Text>
                </Flex>
              )}
              {copyrightForm.typeReason && config.CopyrightFormTypeReason && (
                <Flex flexDirection="column">
                  <Text textStyle="headingItem">
                    {config.CopyrightFormTypeReason.params.labels.main}
                  </Text>
                  <Text textStyle="textBody">{copyrightForm.typeReason}</Text>
                </Flex>
              )}
            </Flex>
          )}
          {copyrightForm.submittingAuthorSignature ||
          copyrightForm.agreement ||
          copyrightForm.authorsFullNames?.length ? (
            <Flex
              flexDirection="column"
              padding={6}
              gap={6}
              borderRadius="base"
              background="neutralPrimaryFill"
            >
              {copyrightForm.submittingAuthorSignature &&
                config.CopyrightFormAuthorSignature && (
                  <Flex flexDirection="column">
                    <Text textStyle="headingItem">
                      {config.CopyrightFormAuthorSignature.params.labels.main}
                    </Text>
                    <Text textStyle="textBody">
                      {copyrightForm.submittingAuthorSignature}
                    </Text>
                  </Flex>
                )}
              {copyrightForm.authorsFullNames?.length
                ? config.CopyrightFormAuthorsFullNames && (
                    <Flex flexDirection="column">
                      <Text textStyle="headingItem">
                        {
                          config.CopyrightFormAuthorsFullNames.params.labels
                            .main
                        }
                      </Text>
                      <Text textStyle="textBody">
                        {copyrightForm.authorsFullNames.join(', ')}
                      </Text>
                    </Flex>
                  )
                : null}
              {copyrightForm.agreement && (
                <>
                  {config.CopyrightFormOAAgreement && (
                    <Flex flexDirection="column" gap={4}>
                      <Checkbox
                        ariaLabel={
                          config.CopyrightFormOAAgreement.params.labels.main
                        }
                        id="waivers"
                        name="waivers"
                        isChecked
                        isDisabled
                      >
                        <Flex alignItems="flex-start" gap={2}>
                          <Text textStyle="headingItem">
                            {parseStringWithLinks(
                              config.CopyrightFormOAAgreement.params.labels.main
                            )}
                          </Text>
                        </Flex>
                        <Text textStyle="textBody">
                          {config.CopyrightFormOAAgreement.params.labels.content
                            ? parseStringWithLinks(
                                config.CopyrightFormOAAgreement.params.labels
                                  .content
                              )
                            : parseStringWithLinks(
                                config.CopyrightFormOAAgreement.params.labels
                                  .subtitle
                              )}
                        </Text>
                      </Checkbox>
                    </Flex>
                  )}
                  {config.CopyrightFormSubscriptionAgreement && (
                    <Flex flexDirection="column" gap={4}>
                      <Checkbox
                        ariaLabel={
                          config.CopyrightFormSubscriptionAgreement.params
                            .labels.main
                        }
                        id="waivers"
                        name="waivers"
                        isChecked
                        isDisabled
                      >
                        <Flex alignItems="flex-start" gap={2}>
                          <Text textStyle="headingItem">
                            {parseStringWithLinks(
                              config.CopyrightFormSubscriptionAgreement.params
                                .labels.main
                            )}
                          </Text>
                        </Flex>
                        <Text textStyle="textBody">
                          {config.CopyrightFormSubscriptionAgreement.params
                            .labels.content
                            ? parseStringWithLinks(
                                config.CopyrightFormSubscriptionAgreement.params
                                  .labels.content
                              )
                            : parseStringWithLinks(
                                config.CopyrightFormSubscriptionAgreement.params
                                  .labels.subtitle
                              )}
                        </Text>
                      </Checkbox>
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          ) : null}
        </Flex>
      )}
    </>
  );
};
